import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { __ } from "services/Translator";
import { enqueueOutbox } from "stores/support/supportSlice";
import { getDraft, setDraft } from "support/storage/supportStorage";
import { newClientRef, newTempId } from "support/hooks/useOutboxPump";
import AttachmentButton from "support/components/AttachmentButton";

const MAX_ROWS = 5;

export default function Composer({ reference, disabled, attachmentsEnabled }) {
  const dispatch = useDispatch();
  const [value, setValue] = useState(() => getDraft(reference));
  const areaRef = useRef(null);

  useEffect(() => {
    setValue(getDraft(reference));
  }, [reference]);

  // The draft outlives the panel, the route and the AuthGuard remount, so a
  // half-typed message is never lost to an accidental close.
  useEffect(() => {
    setDraft(reference, value);
  }, [reference, value]);

  useEffect(() => {
    const el = areaRef.current;
    if (!el) {
      return;
    }
    el.style.height = "auto";
    const lineHeight = 20;
    el.style.height = Math.min(el.scrollHeight, lineHeight * MAX_ROWS + 18) + "px";
  }, [value]);

  const send = () => {
    const body = value.trim();
    if (!body || disabled) {
      return;
    }
    dispatch(
      enqueueOutbox({
        tempId: newTempId(),
        ref: reference,
        body,
        client_ref: newClientRef(),
        at: new Date().toISOString(),
      })
    );
    setValue("");
    setDraft(reference, "");
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  return (
    <div className="supComposer">
      <label className="visuallyHidden" htmlFor={"supComposer-" + reference}>
        {__("Write a message")}
      </label>
      <textarea
        id={"supComposer-" + reference}
        ref={areaRef}
        className="supComposerInput"
        rows={1}
        value={value}
        placeholder={__("Write a message")}
        disabled={disabled}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={onKeyDown}
      />
      <AttachmentButton enabled={attachmentsEnabled} />
      <button
        type="button"
        className="supComposerSend"
        onClick={send}
        disabled={disabled || !value.trim()}
        aria-label={__("Send message")}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
          <path d="M3 20l18-8L3 4v6l12 2-12 2z" fill="currentColor" />
        </svg>
      </button>
    </div>
  );
}
